import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  APIMutationOptions,
  APIMutationResult,
  useAPIMutation,
} from 'shared/api/APIMutation';
import {
  APIPageQueryOptions,
  APIPageQueryResult,
  useAPIPageQuery,
} from 'shared/api/APIPageQuery';
import {
  APIQueryResult,
  useAPIQuery,
  UseAPIQueryOptions,
} from 'shared/api/APIQuery';
import { requestCarrierAPI } from 'shared/api/CarrierAPIClient';
import { useFlaggedSchema } from 'shared/hooks/useFlaggedSchema';
import { useVerificationNotificationsReset } from 'shared/layout/notification-stack/core/HideVerificationNotifications';
import { CarrierInfoDTO } from 'shared/modules/carrier-profile/data/CarrierInfoDTO';
import {
  ACHPaymentInfoDTO,
  ACHPaymentInfoSchema,
  ACHPaymentInfoYupSchema,
  BillingInfoDTO,
  billingInfoSchema,
  billingInfoYupSchema,
  BusinessLicenseDTO,
  businessLicenseSchema,
  businessLicenseYupSchema,
  CargoInsuranceDTO,
  cargoInsuranceSchema,
  cargoInsuranceYupSchema,
  StateMotorCarrierPermitDTO,
  stateMotorCarrierPermitSchema,
  stateMotorCarrierPermitYupSchema,
  TermsAndConditionsDTO,
  TransporterPlateRegistrationDTO,
  transporterPlateRegistrationSchema,
  transporterPlateRegistrationYupSchema,
  W9DTO,
  w9Schema,
  w9YupSchema,
} from './CarrierProfileDTO';

type SendVerificationCodeMethod = 'email' | 'sms';

export function useCarrierProfileAPI() {
  const queryClient = useQueryClient();
  const resetVerificationVisibility = useVerificationNotificationsReset();

  return useMemo(
    () => ({
      updateCarrierInfo: (values: Partial<CarrierInfoDTO>) =>
        requestCarrierAPI('POST /internal/web/carrier/profile/carrier-info/', {
          json: values,
        }).then((response) => {
          void queryClient.invalidateQueries({
            queryKey: ['profile', 'carrier-info'],
          });
          void queryClient.invalidateQueries({
            queryKey: ['verified-carrier-application', 'status'],
          });
          return response;
        }),

      updateInsurance: (values: Partial<CargoInsuranceDTO>) =>
        requestCarrierAPI(
          [
            'PATCH /internal/web/carrier/profile/insurance/{guid}/',
            { guid: values.guid },
          ],
          { json: values },
        ).then((response) => {
          resetVerificationVisibility();
          void queryClient.invalidateQueries({
            queryKey: ['profile', 'insurance-list'],
          });
          void queryClient.invalidateQueries({
            queryKey: ['verified-carrier-application', 'status'],
          });
          return response;
        }),

      createInsurance: (values: Partial<CargoInsuranceDTO>) =>
        requestCarrierAPI('POST /internal/web/carrier/profile/insurance/', {
          json: values,
        }).then((response) => {
          resetVerificationVisibility();
          void queryClient.invalidateQueries({
            queryKey: ['profile', 'insurance-list'],
          });
          void queryClient.invalidateQueries({
            queryKey: ['verified-carrier-application', 'status'],
          });
          return response;
        }),

      updateTermsAndConditions: (values: Partial<TermsAndConditionsDTO>) =>
        requestCarrierAPI(
          'POST /internal/web/carrier/profile/terms-and-conditions/',
          {
            json: values,
          },
        ).then((response) => {
          void queryClient.invalidateQueries({
            queryKey: ['profile', 'terms-and-conditions'],
          });
          void queryClient.invalidateQueries({
            queryKey: ['verified-carrier-application', 'status'],
          });
          return response;
        }),

      updateBillingInfo: (values: Partial<BillingInfoDTO>) =>
        requestCarrierAPI('POST /internal/web/carrier/profile/billing-info/', {
          json: values,
        }).then((response) => {
          void queryClient.invalidateQueries({
            queryKey: ['profile', 'billing-info'],
          });
          void queryClient.invalidateQueries({
            queryKey: ['verified-carrier-application', 'status'],
          });
          return response;
        }),

      updateW9: (values: Partial<W9DTO>) =>
        requestCarrierAPI('POST /internal/web/carrier/profile/w9/', {
          json: values,
        }).then((response) => {
          resetVerificationVisibility();
          void queryClient.invalidateQueries({ queryKey: ['profile', 'w9'] });
          void queryClient.invalidateQueries({
            queryKey: ['verified-carrier-application', 'status'],
          });
          return response;
        }),

      updateTransporterPlateRegistration: (
        values: Partial<TransporterPlateRegistrationDTO>,
      ) =>
        requestCarrierAPI(
          'PATCH /internal/web/carrier/profile/transporter-plate-registration/',
          {
            json: values,
          },
        ).then((response) => {
          resetVerificationVisibility();
          void queryClient.invalidateQueries({
            queryKey: ['profile', 'transporter-plate-registration'],
          });
          void queryClient.invalidateQueries({
            queryKey: ['verified-carrier-application', 'status'],
          });
          return response;
        }),

      updateStateMotorCarrierPermit: (
        values: Partial<StateMotorCarrierPermitDTO>,
      ) =>
        requestCarrierAPI(
          'PATCH /internal/web/carrier/profile/state-motor-carrier-permit/',
          {
            json: values,
          },
        ).then((response) => {
          resetVerificationVisibility();
          void queryClient.invalidateQueries({
            queryKey: ['profile', 'state-motor-carrier-permit'],
          });
          void queryClient.invalidateQueries({
            queryKey: ['verified-carrier-application', 'status'],
          });
          return response;
        }),

      updateBusinessLicense: (values: Partial<BusinessLicenseDTO>) =>
        requestCarrierAPI(
          'PATCH /internal/web/carrier/profile/business-license/',
          {
            json: values,
          },
        ).then((response) => {
          resetVerificationVisibility();
          void queryClient.invalidateQueries({
            queryKey: ['profile', 'business-license'],
          });
          void queryClient.invalidateQueries({
            queryKey: ['verified-carrier-application', 'status'],
          });
          return response;
        }),

      updateACHPaymentInfo: (
        values: Partial<ACHPaymentInfoDTO> & {
          verification_code?: string;
          verification_method?: SendVerificationCodeMethod;
        },
      ) =>
        requestCarrierAPI(
          'POST /internal/web/carrier/profile/ach-payment-info/',
          {
            json: values,
          },
        ).then((response) => {
          void queryClient.invalidateQueries({
            queryKey: ['profile', 'ach-payment-info'],
          });
          void queryClient.invalidateQueries({
            queryKey: ['verified-carrier-application', 'status'],
          });
          return response;
        }),
    }),
    [queryClient, resetVerificationVisibility],
  );
}

export function useUpdateVerificationInfoFromExternalService(
  options?: APIPageQueryOptions<unknown>,
): APIPageQueryResult<unknown> {
  return useAPIPageQuery(
    ['profile', '3rd-party-update'],
    () =>
      requestCarrierAPI(
        'POST /internal/web/carrier/profile/update-from-third-party-service/',
      ),
    {
      ...options,
      gcTime: Infinity,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    },
  );
}

export function useInsurance(
  options?: APIPageQueryOptions<CargoInsuranceDTO>,
): APIPageQueryResult<CargoInsuranceDTO> {
  const schema = useFlaggedSchema(
    'ctms_zod_schema',
    cargoInsuranceYupSchema,
    cargoInsuranceSchema,
  );

  return useAPIPageQuery(
    ['profile', 'insurance-list'],
    () => requestCarrierAPI('GET /internal/web/carrier/profile/insurance/'),
    { schema, ...options },
  );
}

export function useTermsAndConditions(): APIQueryResult<TermsAndConditionsDTO> {
  return useAPIQuery(['profile', 'terms-and-conditions'], () =>
    requestCarrierAPI(
      'GET /internal/web/carrier/profile/terms-and-conditions/',
    ),
  );
}

export function useBillingInfo(): APIQueryResult<BillingInfoDTO> {
  const schema = useFlaggedSchema(
    'ctms_zod_schema',
    billingInfoYupSchema,
    billingInfoSchema,
  );

  return useAPIQuery(
    ['profile', 'billing-info'],
    () => requestCarrierAPI('GET /internal/web/carrier/profile/billing-info/'),
    { schema },
  );
}

export function useW9(
  options?: UseAPIQueryOptions<W9DTO>,
): APIQueryResult<W9DTO> {
  const schema = useFlaggedSchema('ctms_zod_schema', w9YupSchema, w9Schema);

  return useAPIQuery(
    ['profile', 'w9'],
    () => requestCarrierAPI('GET /internal/web/carrier/profile/w9/'),
    { schema, ...options },
  );
}

export function useTransporterPlateRegistration(
  options?: UseAPIQueryOptions<TransporterPlateRegistrationDTO>,
): APIQueryResult<TransporterPlateRegistrationDTO> {
  const schema = useFlaggedSchema(
    'ctms_zod_schema',
    transporterPlateRegistrationYupSchema,
    transporterPlateRegistrationSchema,
  );

  return useAPIQuery(
    ['profile', 'transporter-plate-registration'],
    () =>
      requestCarrierAPI(
        'GET /internal/web/carrier/profile/transporter-plate-registration/',
      ),
    { schema, ...options },
  );
}

export function useStateMotorCarrierPermit(
  options?: UseAPIQueryOptions<StateMotorCarrierPermitDTO>,
): APIQueryResult<StateMotorCarrierPermitDTO> {
  const schema = useFlaggedSchema(
    'ctms_zod_schema',
    stateMotorCarrierPermitYupSchema,
    stateMotorCarrierPermitSchema,
  );

  return useAPIQuery(
    ['profile', 'state-motor-carrier-permit'],
    () =>
      requestCarrierAPI(
        'GET /internal/web/carrier/profile/state-motor-carrier-permit/',
      ),
    { schema, ...options },
  );
}

export function useBusinessLicense(
  options?: UseAPIQueryOptions<BusinessLicenseDTO>,
): APIQueryResult<BusinessLicenseDTO> {
  const schema = useFlaggedSchema(
    'ctms_zod_schema',
    businessLicenseYupSchema,
    businessLicenseSchema,
  );

  return useAPIQuery(
    ['profile', 'business-license'],
    () =>
      requestCarrierAPI('GET /internal/web/carrier/profile/business-license/'),
    { schema, ...options },
  );
}

export function useACHPaymentInfo(
  options?: UseAPIQueryOptions<ACHPaymentInfoDTO>,
): APIQueryResult<ACHPaymentInfoDTO> {
  const schema = useFlaggedSchema(
    'ctms_zod_schema',
    ACHPaymentInfoYupSchema,
    ACHPaymentInfoSchema,
  );

  return useAPIQuery(
    ['profile', 'ach-payment-info'],
    () =>
      requestCarrierAPI('GET /internal/web/carrier/profile/ach-payment-info/'),
    { schema, ...options },
  );
}

export function useDeleteACHPaymentInfo(
  options?: APIMutationOptions<string>,
): APIMutationResult<string> {
  const queryClient = useQueryClient();

  return useAPIMutation(
    () =>
      requestCarrierAPI(
        'DELETE /internal/web/carrier/profile/ach-payment-info/',
      ).then((response) => {
        void queryClient.invalidateQueries({
          queryKey: ['profile', 'ach-payment-info'],
        });
        return response;
      }),
    options,
  );
}

interface SendVerificationCodeResponse {
  recipient: string;
}

export function useSendACHPaymentInfoVerificationCode(
  options?: APIMutationOptions<
    SendVerificationCodeMethod,
    SendVerificationCodeResponse
  >,
): APIMutationResult<SendVerificationCodeMethod, SendVerificationCodeResponse> {
  return useAPIMutation(
    (method) =>
      requestCarrierAPI(
        'POST /internal/web/carrier/profile/ach-payment-info/send-verification-code/',
        { json: { method } },
      ),
    options,
  );
}

export function useVerificationFmcsaSync(options?: APIMutationOptions) {
  const queryClient = useQueryClient();

  return useAPIMutation(
    () =>
      requestCarrierAPI(
        'POST /internal/web/carrier/profile/sync-to-fmcsa/',
      ).then((response) => {
        void queryClient.invalidateQueries({
          queryKey: ['verified-carrier-application', 'status'],
        });
        return response;
      }),
    options,
  );
}
